import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import YouTubeModal from "../components/YouTubeModal";
import { getReliableThumbnailUrl, handleThumbnailError } from "../utils/youtubeThumbnail";
import { isContentVisible } from "../utils/contentVisibility";
import { defaultSiteSettings, SiteSettings } from "../utils/siteSettings";
import { defaultSiteCopy, SiteCopy } from "../utils/siteCopy";

// Set axios base URL
const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
    ? ""
    : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

const HomeContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

const ContentSection = styled.div`
    margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    color: #333;
    margin-bottom: 1.5rem;
    text-align: center;
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    margin-bottom: 2rem;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
    }

    @media (max-width: 480px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 0.8rem;
    }
`;

const ContentCard = styled.div`
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: transform 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: translateY(-5px);
    }
`;

const ContentThumbnail = styled.img`
    width: 100%;
    height: 200px;
    object-fit: cover;
`;

const ContentInfo = styled.div`
    padding: 1.5rem;
`;

const ContentTitle = styled.h3`
    font-size: 1.2rem;
    color: #333;
    margin: 0 0 0.5rem 0;
`;

const ContentDescription = styled.p`
    color: #666;
    font-size: 0.9rem;
    margin: 0 0 1rem 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const DetailLink = styled(Link)`
    display: inline-flex;
    margin-top: 0.85rem;
    color: #234f7a;
    font-size: 0.9rem;
    font-weight: 700;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const ContentMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #999;
    font-size: 0.8rem;
`;


const FilterContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 2rem;
    justify-content: center;
    flex-wrap: nowrap;

    @media (max-width: 768px) {
        justify-content: flex-start;
        overflow-x: auto;
        padding: 0 1rem;
        margin: 0 -1rem 2rem -1rem;

        /* Hide scrollbar but keep functionality */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* Internet Explorer 10+ */

        &::-webkit-scrollbar {
            display: none; /* WebKit */
        }

        /* Add padding to first and last items for better spacing */
        &::before,
        &::after {
            content: '';
            flex-shrink: 0;
            width: 1rem;
        }
    }
`;

const SearchContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
`;

const SearchInput = styled.input`
    padding: 0.75rem 1rem;
    border: 2px solid #ddd;
    border-radius: 25px;
    font-size: 1rem;
    width: 100%;
    max-width: 400px;
    outline: none;
    transition: border-color 0.3s ease;

    &:focus {
        border-color: #667eea;
    }

    @media (max-width: 768px) {
        padding: 0.6rem 0.8rem;
        font-size: 0.9rem;
    }
`;

const FilterButton = styled.button.withConfig({
    shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>`
    padding: 0.5rem 1rem;
    border: 2px solid ${(props) => (props.active ? "#667eea" : "#ddd")};
    background: ${(props) => (props.active ? "#667eea" : "white")};
    color: ${(props) => (props.active ? "white" : "#333")};
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.3s ease;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        border-color: #667eea;
        background: ${(props) => (props.active ? "#5a6fd8" : "#f8f9fa")};
    }

    @media (max-width: 768px) {
        padding: 0.4rem 0.8rem;
        font-size: 0.8rem;
        min-width: auto;
    }

    @media (max-width: 480px) {
        padding: 0.35rem 0.7rem;
        font-size: 0.75rem;
    }
`;

const CategoryIntro = styled.div`
    margin: 0 0 2rem;
    padding: 1.5rem 0;
    border-top: 1px solid #e2e7ec;
    border-bottom: 1px solid #e2e7ec;
`;

const CategoryEyebrow = styled.p`
    margin: 0 0 0.5rem;
    color: #667eea;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const CategoryTitle = styled.h3`
    margin: 0;
    color: #18212f;
    font-size: clamp(1.6rem, 3vw, 2.4rem);
    line-height: 1.1;
`;

const CategoryCopy = styled.p`
    max-width: 760px;
    margin: 0.85rem 0 0;
    color: #5d6876;
    line-height: 1.7;
`;

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: "works" | "product" | "pr";
    category_id?: number;
    category_name?: string;
    category_color?: string;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string;
    duration: string;
    published_at: string;
    view_count: number;
    like_count: number;
    custom_thumbnail: string;
    custom_description: string;
    custom_published_at: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

const Works: React.FC = () => {
    const [content, setContent] = useState<Content[]>([]);
    const [allContent, setAllContent] = useState<Content[]>([]);
    const [categories, setCategories] = useState<{id: number, name: string, description: string, color: string}[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedContent, setSelectedContent] = useState<Content | null>(null);
    const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isSearching, setIsSearching] = useState(false);
    const [siteSettings, setSiteSettings] = useState<SiteSettings>(defaultSiteSettings);
    const [siteCopy, setSiteCopy] = useState<SiteCopy>(defaultSiteCopy);
    const currentCategory = selectedCategory
        ? categories.find((category) => category.name === selectedCategory)
        : null;

    useEffect(() => {
        fetchContent();
        fetchCategories();
        fetchSiteSettings();
        fetchSiteCopy();
    }, []);

    const fetchSiteSettings = async () => {
        try {
            const response = await axios.get("/api/site-settings");
            setSiteSettings({ ...defaultSiteSettings, ...(response.data.data || {}) });
        } catch (error) {
            console.error("Error fetching site settings:", error);
        }
    };

    const fetchSiteCopy = async () => {
        try {
            const response = await axios.get("/api/site-copy");
            setSiteCopy({ ...defaultSiteCopy, ...(response.data.data || {}) });
        } catch (error) {
            console.error("Error fetching site copy:", error);
        }
    };

    useEffect(() => {
        if (searchQuery.trim()) {
            // If searching, don't filter by category here as search API handles it
            return;
        }

        if (selectedCategory) {
            setContent(allContent.filter(item => item.category_name === selectedCategory));
        } else {
            setContent(allContent);
        }
    }, [selectedCategory, allContent, searchQuery]);

    const fetchContent = async () => {
        try {
            // Only fetch works content for home page
            const response = await axios.get(`/api/content?type=works`);
            console.log("Works content response:", response.data);
            if (response.data.success) {
                const visibleContent = (response.data.data || []).filter(isContentVisible);
                setAllContent(visibleContent);
                setContent(visibleContent);
            } else {
                console.error("Content API returned error:", response.data);
            }
        } catch (error) {
            console.error("Error fetching content:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get("/api/categories");
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleSearch = async (query: string) => {
        try {
            setIsSearching(true);
            console.log("Searching for:", query);

            if (!query.trim()) {
                // If empty search, reset to all content
                setContent(allContent);
                setSearchQuery("");
                return;
            }

            const response = await axios.get(`/api/search?q=${encodeURIComponent(query)}&type=works`);
            console.log("Search response:", response.data);

            if (response.data.success) {
                setContent((response.data.data || []).filter(isContentVisible));
                setSearchQuery(query);
            } else {
                console.error("Search API returned error:", response.data);
            }
        } catch (error) {
            console.error("Error searching content:", error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;

        // Debounce search - only search after user stops typing for 500ms
        const timeoutId = setTimeout(() => {
            handleSearch(query);
        }, 500);

        // Clear previous timeout
        return () => clearTimeout(timeoutId);
    };

    const handleContentClick = (content: Content) => {
        setSelectedContent(content);
        setIsYouTubeModalOpen(true);
    };

    const handleCloseYouTubeModal = () => {
        setIsYouTubeModalOpen(false);
        setSelectedContent(null);
    };

    const handleCategoryClick = (category: string | null) => {
        setSelectedCategory(category);
        // Clear search when clicking category
        setSearchQuery("");
    };

    const renderWorksContent = () => {
        return (
            <ContentSection>
                <SectionTitle>Works</SectionTitle>

                {/* Search Input - Hidden */}
                {/*
                <SearchContainer>
                    <SearchInput
                        type="text"
                        placeholder="콘텐츠 검색..."
                        onChange={handleSearchInputChange}
                        value={searchQuery}
                    />
                </SearchContainer>
                */}

                {/* Category Filter - Force rebuild */}
                <FilterContainer>
                    <FilterButton
                        active={selectedCategory === null}
                        onClick={() => handleCategoryClick(null)}
                    >
                        All
                    </FilterButton>
                    {categories.map((category) => (
                        <FilterButton
                            key={category.id}
                            active={selectedCategory === category.name}
                            onClick={() => handleCategoryClick(category.name)}
                        >
                            {category.name}
                        </FilterButton>
                    ))}
                </FilterContainer>

                {siteSettings.show_category_intro && (currentCategory || siteCopy.works_intro_title || siteCopy.works_intro_body) && (
                    <CategoryIntro>
                        <CategoryEyebrow>{currentCategory ? "Category Focus" : "Portfolio Focus"}</CategoryEyebrow>
                        {(currentCategory?.name || siteCopy.works_intro_title) && <CategoryTitle>{currentCategory?.name || siteCopy.works_intro_title}</CategoryTitle>}
                        {(currentCategory?.description || siteCopy.works_intro_body) && (
                            <CategoryCopy>{currentCategory?.description || siteCopy.works_intro_body}</CategoryCopy>
                        )}
                    </CategoryIntro>
                )}

                {isSearching ? (
                    <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                        검색 중...
                    </div>
                ) : content.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
                        {searchQuery ? "검색 결과가 없습니다." : "선택한 카테고리에 콘텐츠가 없습니다."}
                    </div>
                ) : (
                    <ContentGrid>
                        {content.map((item) => (
                        <ContentCard key={item.id} onClick={() => handleContentClick(item)}>
	                            <ContentThumbnail
	                                src={getReliableThumbnailUrl(item.custom_thumbnail || item.thumbnail_url)}
	                                alt={item.title}
	                                onError={(e) => handleThumbnailError(e.currentTarget)}
	                            />
                            <ContentInfo>
                                <ContentTitle>{item.title}</ContentTitle>
                                <ContentDescription>{item.custom_description || item.description}</ContentDescription>
                                <ContentMeta>
                                    <span>
                                        {item.category_name && `${item.category_name} • `}
                                        {item.duration && `${item.duration} • `}
                                        {item.view_count > 0 && `${item.view_count.toLocaleString()} views`}
                                    </span>
                                    <span>{new Date(item.custom_published_at || item.published_at || item.created_at).toLocaleDateString()}</span>
                                </ContentMeta>
                                {siteSettings.show_project_detail && (
                                    <DetailLink to={`/works/detail/${item.id}`} onClick={(event) => event.stopPropagation()}>
                                        프로젝트 상세 보기
                                    </DetailLink>
                                )}
                            </ContentInfo>
                        </ContentCard>
                    ))}
                    </ContentGrid>
                )}
            </ContentSection>
        );
    };

    return (
        <HomeContainer>
            {renderWorksContent()}

            <YouTubeModal content={selectedContent} isOpen={isYouTubeModalOpen} onClose={handleCloseYouTubeModal} />
        </HomeContainer>
    );
};

export default Works;
